import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type Lang = 'is' | 'en';

/** Every string the web app shows, in Icelandic (default) and English. Short labels; no explanations. */
export const dict = {
  appName: { is: 'Mynstra BIM', en: 'Mynstra BIM' },
  signIn: { is: 'Skrá inn', en: 'Sign in' },
  signOut: { is: 'Skrá út', en: 'Sign out' },
  email: { is: 'Netfang', en: 'Email' },
  sendLink: { is: 'Senda hlekk', en: 'Send link' },
  microsoft: { is: 'Microsoft', en: 'Microsoft' },
  linkSent: {
    is: 'Ef netfangið hefur aðgang færðu hlekk í pósti. Hann gildir í 15 mínútur.',
    en: 'If the address has access, a link is on its way. It works for 15 minutes.',
  },
  otherEmail: { is: 'Annað netfang', en: 'Use another address' },
  loginForInvite: {
    is: 'Notaðu netfangið sem boðið var sent á.',
    en: 'Use the address the invitation was sent to.',
  },
  signInError: { is: 'Innskráning tókst ekki', en: 'Sign-in failed' },
  confirmTitle: { is: 'Staðfesta innskráningu', en: 'Confirm sign-in' },
  badLink: {
    is: 'Hlekkurinn er ógildur. Biddu um nýjan.',
    en: 'The link is not valid. Ask for a new one.',
  },
  consentTitle: { is: 'Veita aðgang', en: 'Grant access' },
  consentText: { is: 'vill fá aðgang fyrir þína hönd.', en: 'wants access on your behalf.' },
  allow: { is: 'Leyfa', en: 'Allow' },
  deny: { is: 'Hafna', en: 'Deny' },
  scopes: { is: 'Heimildir', en: 'Scopes' },
  cliTitle: { is: 'Tengja p2b', en: 'Connect p2b' },
  cliText: {
    is: 'Leyfa p2b á þessari tölvu að vinna fyrir þig?',
    en: 'Let p2b on this computer act for you?',
  },
  cliDone: { is: 'Tengt. Þú mátt loka glugganum.', en: 'Connected. You can close this window.' },
  projects: { is: 'Verkefni', en: 'Projects' },
  organizations: { is: 'Fyrirtæki', en: 'Organizations' },
  noProjects: { is: 'Engin verkefni.', en: 'No projects.' },
  newProject: { is: 'Nýtt verkefni', en: 'New project' },
  newOrg: { is: 'Nýtt fyrirtæki', en: 'New organization' },
  name: { is: 'Heiti', en: 'Name' },
  slug: { is: 'Auðkenni', en: 'Slug' },
  address: { is: 'Heimilisfang', en: 'Address' },
  landNumber: { is: 'Landnúmer', en: 'Land number' },
  municipality: { is: 'Sveitarfélag', en: 'Municipality' },
  ownerEmail: { is: 'Netfang eiganda', en: 'Owner email' },
  create: { is: 'Stofna', en: 'Create' },
  cancel: { is: 'Hætta við', en: 'Cancel' },
  save: { is: 'Vista', en: 'Save' },
  members: { is: 'Meðlimir', en: 'Members' },
  role: { is: 'Hlutverk', en: 'Role' },
  files: { is: 'Skrár', en: 'Files' },
  revisions: { is: 'Útgáfur', en: 'Revisions' },
  publications: { is: 'Birtingar', en: 'Publications' },
  access: { is: 'Aðgangur', en: 'Access' },
  audit: { is: 'Atvikaskrá', en: 'Audit log' },
  search: { is: 'Leita', en: 'Search' },
  searchHint: {
    is: 'Slóð eða lýsigögn, t.d. status=Samþykkt',
    en: 'Path or metadata, e.g. status=Samþykkt',
  },
  allKinds: { is: 'Allar tegundir', en: 'All kinds' },
  showDeleted: { is: 'Sýna eyddar', en: 'Show deleted' },
  noFiles: { is: 'Engar skrár.', en: 'No files.' },
  selectFile: { is: 'Veldu skrá.', en: 'Select a file.' },
  kind: { is: 'Tegund', en: 'Kind' },
  visibility: { is: 'Sýnileiki', en: 'Visibility' },
  size: { is: 'Stærð', en: 'Size' },
  version: { is: 'Útgáfa', en: 'Version' },
  versions: { is: 'Útgáfusaga', en: 'History' },
  metadata: { is: 'Lýsigögn', en: 'Metadata' },
  source: { is: 'Uppruni', en: 'Source' },
  download: { is: 'Sækja', en: 'Download' },
  markDeleted: { is: 'Merkja eytt', en: 'Mark deleted' },
  confirmDelete: {
    is: 'Merkja skrána eydda? Útgáfurnar haldast.',
    en: 'Mark the file deleted? Its versions are kept.',
  },
  deleted: { is: 'Eytt', en: 'Deleted' },
  editMetadata: { is: 'Breyta lýsigögnum', en: 'Edit metadata' },
  metadataHelp: { is: 'JSON. Gildið null fjarlægir lykil.', en: 'JSON. A null value removes a key.' },
  internal: { is: 'Innra', en: 'Internal' },
  publishable: { is: 'Má afhenda', en: 'Deliverable' },
  makeInternal: { is: 'Gera innra', en: 'Make internal' },
  makePublishable: { is: 'Leyfa afhendingu', en: 'Allow delivery' },
  channel: { is: 'Leið', en: 'Via' },
  created: { is: 'Stofnað', en: 'Created' },
  viewer: { is: 'Skoðari', en: 'Viewer' },
  openViewer: { is: 'Opna líkanið', en: 'Open the model' },
  pages: { is: 'Síður', en: 'Pages' },
  draftPages: { is: 'Drög', en: 'Drafts' },
  noPages: { is: 'Engar síður.', en: 'No pages.' },
  pageGone: {
    is: 'Síðan er ekki til eða ekki lengur birt.',
    en: 'The page does not exist or is no longer published.',
  },
  pagesToPublish: { is: 'Síður', en: 'Pages' },
  internalPage: { is: 'innri', en: 'internal' },
  draftPreview: { is: 'Síður í vinnslu', en: 'Pages in progress' },
  publish: { is: 'Birta', en: 'Publish' },
  publishRevision: { is: 'Birta útgáfu', en: 'Publish a revision' },
  revision: { is: 'Útgáfa', en: 'Revision' },
  title: { is: 'Titill', en: 'Title' },
  notes: { is: 'Skýringar', en: 'Notes' },
  withdraw: { is: 'Draga til baka', en: 'Withdraw' },
  confirmWithdraw: {
    is: 'Draga birtinguna til baka? Viðskiptavinir missa aðgang strax.',
    en: 'Withdraw the publication? Clients lose access at once.',
  },
  withdrawn: { is: 'Dregið til baka', en: 'Withdrawn' },
  earlier: { is: 'Eldri birtingar', en: 'Earlier' },
  noPublications: { is: 'Engar birtingar enn.', en: 'No publications yet.' },
  noRevisions: { is: 'Engar útgáfur.', en: 'No revisions.' },
  published: { is: 'Birt', en: 'Published' },
  isPublished: { is: 'birt', en: 'published' },
  basis: { is: 'Grunnur', en: 'Basis' },
  checks: { is: 'Prófanir', en: 'Checks' },
  checksPassed: { is: 'Stóðust', en: 'Passed' },
  checksFailed: { is: 'Féllu', en: 'Failed' },
  path: { is: 'Slóð', en: 'Path' },
  who: { is: 'Hver', en: 'Who' },
  granted: { is: 'Veitt', en: 'Granted' },
  grant: { is: 'Veita aðgang', en: 'Grant access' },
  revoke: { is: 'Afturkalla', en: 'Revoke' },
  notSignedIn: { is: 'Ekki skráð(ur) inn enn', en: 'Not signed in yet' },
  person: { is: 'Manneskja', en: 'Person' },
  organization: { is: 'Fyrirtæki', en: 'Organization' },
  notify: { is: 'Senda póst', en: 'Send email' },
  when: { is: 'Hvenær', en: 'When' },
  action: { is: 'Aðgerð', en: 'Action' },
  target: { is: 'Viðfang', en: 'Target' },
  more: { is: 'Nánar', en: 'Details' },
  older: { is: 'Sýna eldri', en: 'Show older' },
  apiKeys: { is: 'API-lyklar', en: 'API keys' },
  newKey: { is: 'Búa til lykil', en: 'Create key' },
  scope: { is: 'Heimild', en: 'Scope' },
  read: { is: 'Lesa', en: 'Read' },
  write: { is: 'Lesa og skrifa', en: 'Read and write' },
  validFor: { is: 'Gildir', en: 'Valid for' },
  expires: { is: 'Rennur út', en: 'Expires' },
  never: { is: 'Aldrei', en: 'Never' },
  lastUsed: { is: 'Síðast notaður', en: 'Last used' },
  copyKey: {
    is: 'Afritaðu lykilinn núna. Hann sést ekki aftur.',
    en: 'Copy the key now. It will not be shown again.',
  },
  copy: { is: 'Afrita', en: 'Copy' },
  copied: { is: 'Afritað', en: 'Copied' },
  delete: { is: 'Eyða', en: 'Delete' },
  days: { is: 'dagar', en: 'days' },
  waitingTitle: { is: 'Bíður aðgangs', en: 'Waiting for access' },
  waitingText: {
    is: 'Mynstra veitir aðgang. Fékkstu boð í pósti? Opnaðu hlekkinn í því.',
    en: 'Mynstra gives access. Got an invitation by email? Open its link.',
  },
  pendingUsers: { is: 'Bíða aðgangs', en: 'Waiting for access' },
  noPendingUsers: { is: 'Enginn bíður.', en: 'Nobody is waiting.' },
  assign: { is: 'Úthluta', en: 'Assign' },
  verified: { is: 'Staðfest', en: 'Verified' },
  invite: { is: 'Senda boð', en: 'Send invitation' },
  projectRole: { is: 'Hlutverk í verkefni', en: 'Project role' },
  none: { is: 'Ekkert', en: 'None' },
  inviteSent: { is: 'Boð sent.', en: 'Invitation sent.' },
  invitationTitle: { is: 'Boð', en: 'Invitation' },
  invitations: { is: 'Boð', en: 'Invitations' },
  noInvitations: { is: 'Engin boð.', en: 'No invitations.' },
  noMembers: { is: 'Engir meðlimir enn.', en: 'No members yet.' },
  inviteMember: { is: 'Bjóða', en: 'Invite' },
  // The organization page (2026-11-01).
  settings: { is: 'Stillingar', en: 'Settings' },
  kennitala: { is: 'Kennitala', en: 'Company ID (kennitala)' },
  kt: { is: 'kt.', en: 'ID' },
  slugLabel: { is: 'Stutt heiti (slóð)', en: 'Short name (URL)' },
  profile: { is: 'Upplýsingar', en: 'Profile' },
  profileHint: { is: 'Birtast í haus síðunnar.', en: 'Shown in the page header.' },
  saved: { is: 'Vistað.', en: 'Saved.' },
  verifiedDomains: { is: 'Staðfest lén', en: 'Verified domains' },
  domainsHint: {
    is: 'Fólk með netfang á léninu kemst inn án boðs.',
    en: 'People with an address on the domain join without an invitation.',
  },
  noDomains: { is: 'Engin lén.', en: 'No domains.' },
  domainsByMynstra: { is: 'Mynstra skráir lén.', en: 'Mynstra registers domains.' },
  newJoinAs: { is: 'Nýir fá hlutverkið', en: 'New people join as' },
  tenantLinked: { is: 'Microsoft-tenant tengt', en: 'Microsoft tenant linked' },
  searchProjects: { is: 'Leita í verkefnum', en: 'Search projects' },
  searchPeople: { is: 'Leita að nafni eða netfangi', en: 'Search name or email' },
  all: { is: 'Öll', en: 'All' },
  allPeople: { is: 'Allir', en: 'All' },
  inProgress: { is: 'Í vinnslu', en: 'In progress' },
  delivered: { is: 'Afhent', en: 'Delivered' },
  status: { is: 'Staða', en: 'Status' },
  // "Afhending" since the project page's terminology (2026-11-01): one word on every page.
  latestPublication: { is: 'Nýjasta afhending', en: 'Latest delivery' },
  noPublication: { is: 'Engin afhending', en: 'No delivery' },
  lastActivity: { is: 'Síðast breytt', en: 'Last activity' },
  yourRole: { is: 'Hlutverk þitt', en: 'Your role' },
  // Home page (2026-11-01)
  waitingOnYou: { is: 'Bíður þín', en: 'Waiting on you' },
  usersWaiting: { is: '{n} bíða aðgangs', en: '{n} waiting for access' },
  userWaiting: { is: '1 bíður aðgangs', en: '1 waiting for access' },
  frozenOn: { is: 'fryst {date}, ekki afhent', en: 'frozen {date}, not delivered' },
  inboxNew: { is: '{n} nýjar skrár frá samstarfsaðila', en: '{n} new files from a partner' },
  inboxNewOne: { is: '1 ný skrá frá samstarfsaðila', en: '1 new file from a partner' },
  allOrgs: { is: 'Öll fyrirtæki', en: 'All organizations' },
  viewList: { is: 'Listi', en: 'List' },
  viewMap: { is: 'Kort', en: 'Map' },
  view: { is: 'Sýn', en: 'View' },
  noLocation: { is: 'Án staðsetningar', en: 'Not on the map' },
  open: { is: 'Opna', en: 'Open' },
  changedWhen: { is: 'Breytt {when}', en: 'Changed {when}' },
  editProject: { is: 'Breyta verkefni', en: 'Edit project' },
  edit: { is: 'Breyta', en: 'Edit' },
  location: { is: 'Staðsetning', en: 'Location' },
  location_georef: { is: 'Úr georef.json', en: 'From georef.json' },
  location_map: { is: 'Sett á korti', en: 'Placed on the map' },
  location_registry: { is: 'Úr staðfangaskrá', en: 'From the address register' },
  locationHint: {
    is: 'Smelltu á kortið til að setja pinna. Dragðu hann til að færa.',
    en: 'Click the map to place a pin. Drag it to move.',
  },
  findInRegistry: { is: 'Finna í staðfangaskrá', en: 'Find in the address register' },
  registry: { is: 'Staðfangaskrá', en: 'Address register' },
  registryNone: { is: 'Ekkert staðfang fannst.', en: 'No address found.' },
  noLocationYet: { is: 'Ekki á korti.', en: 'Not on the map.' },
  basemapMissing: { is: 'Kortagrunnur náðist ekki.', en: 'The basemap could not be loaded.' },
  zoomIn: { is: 'Þysja inn', en: 'Zoom in' },
  zoomOut: { is: 'Þysja út', en: 'Zoom out' },
  sortBy: { is: 'Raða eftir {col}', en: 'Sort by {col}' },
  noOrgs: { is: 'Engin fyrirtæki.', en: 'No organizations.' },
  noMatch: { is: 'Ekkert passar við leitina.', en: 'Nothing matches.' },
  active: { is: 'Virkir', en: 'Active' },
  memberActive: { is: 'Virkur', en: 'Active' },
  invitationSent: { is: 'Boð sent', en: 'Invited' },
  lastSignIn: { is: 'Síðast inni', en: 'Last sign-in' },
  allProjects: { is: 'Öll', en: 'All' },
  today: { is: 'í dag', en: 'today' },
  yesterday: { is: 'í gær', en: 'yesterday' },
  daysAgo: { is: 'fyrir {n} dögum', en: '{n} days ago' },
  // Nominative only: names are not declined.
  invitedByUntil: { is: '{who} bauð, gildir til {date}', en: 'Invited by {who}, valid until {date}' },
  invitationExpired: { is: 'Útrunnið', en: 'Expired' },
  andMore: { is: '+{n}', en: '+{n}' },
  expiredOn: { is: 'Rann út {date}', en: 'Expired {date}' },
  rowActions: { is: 'Aðgerðir', en: 'Actions' },
  changeRole: { is: 'Breyta hlutverki', en: 'Change role' },
  removeFromOrg: { is: 'Fjarlægja úr fyrirtæki', en: 'Remove from organization' },
  removeConfirm: {
    is: '{who} missir aðgang að {org} og verkefnum þess. Boð þarf til að koma aftur.',
    en: '{who} loses access to {org} and its projects. Coming back needs a new invitation.',
  },
  remove: { is: 'Fjarlægja', en: 'Remove' },
  inviteTo: { is: 'Bjóða í {org}', en: 'Invite to {org}' },
  emails: { is: 'Netföng', en: 'Email addresses' },
  emailsHint: {
    is: 'Eitt eða fleiri, aðskilin með kommu eða línubili',
    en: 'One or more, separated by commas or lines',
  },
  orgRole: { is: 'Hlutverk í fyrirtækinu', en: 'Role in the organization' },
  onlyOrgProjects: { is: 'Aðeins verkefni {org}', en: 'Only {org}’s projects' },
  sendInvites: { is: 'Senda {n} boð', en: 'Send {n} invitations' },
  sendInvite: { is: 'Senda boð', en: 'Send invitation' },
  invitesSent: { is: '{n} boð send.', en: '{n} invitations sent.' },
  inviteFailed: { is: 'Ekki tókst að bjóða {email}', en: 'Could not invite {email}' },
  badEmails: { is: 'Ógild netföng: {list}', en: 'Not valid: {list}' },
  close: { is: 'Loka', en: 'Close' },
  resend: { is: 'Senda aftur', en: 'Resend' },
  resent: { is: 'Sent aftur.', en: 'Sent again.' },
  invitedBy: { is: 'Boðið af', en: 'Invited by' },
  validUntil: { is: 'Gildir til', en: 'Valid until' },
  status_pending: { is: 'bíður', en: 'pending' },
  status_expired: { is: 'útrunnið', en: 'expired' },
  status_canceled: { is: 'afturkallað', en: 'revoked' },
  status_accepted: { is: 'samþykkt', en: 'accepted' },
  status_rejected: { is: 'hafnað', en: 'declined' },
  inviteIntro: { is: 'býður þér aðgang að Mynstra BIM.', en: 'invites you to Mynstra BIM.' },
  inviteFor: { is: 'Fyrir', en: 'For' },
  signInToAccept: { is: 'Skrá inn til að samþykkja', en: 'Sign in to accept' },
  wrongAccount: {
    is: 'Þú ert skráð(ur) inn með öðru netfangi. Skráðu þig inn með netfanginu sem boðið var sent á.',
    en: 'You are signed in with another address. Sign in with the address the invitation was sent to.',
  },
  switchAccount: { is: 'Skipta um aðgang', en: 'Switch account' },
  invitationGone: {
    is: 'Boðið er ekki lengur í gildi. Biddu um nýtt.',
    en: 'This invitation is no longer valid. Ask for a new one.',
  },
  acceptAndOpen: { is: 'Samþykkja', en: 'Accept' },
  youHaveInvitations: { is: 'Boð til þín', en: 'Invitations for you' },
  role_owner: { is: 'eigandi', en: 'owner' },
  role_admin: { is: 'stjórnandi', en: 'admin' },
  role_member: { is: 'meðlimur', en: 'member' },
  role_manager: { is: 'verkefnisstjóri', en: 'manager' },
  role_editor: { is: 'ritstjóri', en: 'editor' },
  role_reviewer: { is: 'yfirlesari', en: 'reviewer' },
  role_partner: { is: 'samstarfsaðili', en: 'partner' },
  role_client: { is: 'viðskiptavinur', en: 'client' },
  forbidden: { is: 'Þú hefur ekki aðgang að þessu.', en: 'You do not have access to this.' },
  notFound: { is: 'Fannst ekki.', en: 'Not found.' },
  error: { is: 'Villa', en: 'Error' },
  loading: { is: 'Hleð…', en: 'Loading…' },
  back: { is: 'Til baka', en: 'Back' },
  language: { is: 'English', en: 'Íslenska' },
  basisNote: {
    is: 'Líkanið er byggt á samþykktum teikningum, ekki uppmælingu.',
    en: 'The model is built from the approved drawings, not from a survey.',
  },
  // Project page (2026-11): one deliveries tab. A delivery is a frozen revision; delivered = published.
  deliveries: { is: 'Afhendingar', en: 'Deliveries' },
  model: { is: 'Líkan', en: 'Model' },
  delivery: { is: 'Afhending', en: 'Delivery' },
  deliver: { is: 'Afhenda', en: 'Deliver' },
  deliverTitle: { is: 'Afhenda verkkaupa', en: 'Deliver to the client' },
  frozen: { is: 'Fryst', en: 'Frozen' },
  withdrawnState: { is: 'Dregin til baka', en: 'Withdrawn' },
  lastDelivered: { is: 'Síðast afhent', en: 'Last delivered' },
  frozenNotDelivered: { is: 'Fryst, ekki afhent', en: 'Frozen, not delivered' },
  client: { is: 'Verkkaupi', en: 'Client' },
  allDeliveries: { is: 'Allar afhendingar', en: 'All deliveries' },
  earlierDeliveries: { is: 'Eldri afhendingar', en: 'Earlier deliveries' },
  noDeliveries: { is: 'Ekkert afhent enn.', en: 'Nothing delivered yet.' },
  noFrozen: {
    is: 'Ekkert fryst enn. Frystu stöðuna með p2b revision create.',
    en: 'Nothing frozen yet. Freeze the state with p2b revision create.',
  },
  reports: { is: 'Skýrslur', en: 'Reports' },
  downloads: { is: 'Niðurhal', en: 'Downloads' },
  openInBrowser: { is: 'Opna í vafra', en: 'Open in the browser' },
  from: { is: 'Frá', en: 'From' },
  to: { is: 'Til', en: 'To' },
  project: { is: 'Verkefni', en: 'Project' },
  frozenState: { is: 'Fryst ástand', en: 'Frozen state' },
  whatClientGets: { is: 'Það sem verkkaupi fær', en: 'What the client gets' },
  modelInBrowser: { is: 'Líkanið í vafra', en: 'The model in the browser' },
  noModel: { is: 'Ekkert líkan í vafra', en: 'No model in the browser' },
  titleHint: { is: 'Það sem verkkaupi sér efst.', en: 'What the client sees at the top.' },
  immutableHint: {
    is: 'Afhendingu er ekki hægt að breyta eftir á, aðeins draga til baka.',
    en: 'A delivery cannot be changed afterwards, only withdrawn.',
  },
  replacesCurrent: {
    is: '„{title}“ færist undir eldri afhendingar.',
    en: '“{title}” moves to earlier deliveries.',
  },
  withdrawTitle: { is: 'Draga afhendingu til baka?', en: 'Withdraw the delivery?' },
  withdrawText: {
    is: '„{title}“ hverfur frá verkkaupa. Líkanið og niðurhalið lokast strax.',
    en: '“{title}” disappears for the client. The model and the downloads close at once.',
  },
  withdrawKeep: {
    is: 'Frysta ástandið og atvikaskráin halda sér. Hægt er að afhenda aftur.',
    en: 'The frozen state and the audit log stay. It can be delivered again.',
  },
  withdrawAction: { is: 'Draga til baka…', en: 'Withdraw…' },
  moreActions: { is: 'Fleiri aðgerðir', en: 'More actions' },
  sincePrevious: { is: 'Frá fyrri', en: 'Since previous' },
  added: { is: 'Bætt við', en: 'Added' },
  changed: { is: 'Breytt', en: 'Changed' },
  removed: { is: 'Fjarlægt', en: 'Removed' },
  firstFrozen: { is: 'fyrsta', en: 'first' },
  andMorePaths: { is: '+ {n} aðrar', en: '+ {n} more' },
  checksOf: { is: '{a}/{b} stóðust', en: '{a}/{b} passed' },
  checksLog: { is: 'Prófanaskrá', en: 'Checks log' },
  frozenBy: { is: 'Fryst af', en: 'Frozen by' },
  people: { is: 'notendur', en: 'people' },
  showWorkspace: { is: 'Sýna vinnuskrár', en: 'Show workspace files' },
  workspaceHidden: { is: '{n} vinnuskrár faldar.', en: '{n} workspace files hidden.' },
  allFiles: { is: 'Allar skrár', en: 'All files' },
  results: { is: '{n} skrár', en: '{n} files' },
  updated: { is: 'Uppfært', en: 'Updated' },
  by: { is: 'Af', en: 'By' },
  ver: { is: 'Útg.', en: 'Ver.' },
  emptyFolder: { is: 'Engar skrár hér.', en: 'No files here.' },
  internalLong: { is: 'Innra: aðeins Mynstra og rýnar', en: 'Internal: Mynstra and reviewers only' },
  publishableLong: { is: 'Má afhenda verkkaupa', en: 'May be delivered to the client' },
  latest: { is: 'Nýjasta', en: 'Latest' },
  'kind_source-drawing': { is: 'Teikning', en: 'Drawing' },
  'kind_source-index': { is: 'Teikningaskrá', en: 'Drawing index' },
  kind_raster: { is: 'Rastamynd', en: 'Raster' },
  kind_overlay: { is: 'Yfirlögn', en: 'Overlay' },
  kind_photo: { is: 'Ljósmynd', en: 'Photo' },
  'kind_point-cloud': { is: 'Punktaský', en: 'Point cloud' },
  kind_reading: { is: 'Aflestur', en: 'Reading' },
  kind_ledger: { is: 'Færsluskrá', en: 'Ledger' },
  kind_report: { is: 'Skýrsla', en: 'Report' },
  kind_critic: { is: 'Rýni', en: 'Review' },
  'kind_model-source': { is: 'Líkanskóði', en: 'Model source' },
  'kind_model-build': { is: 'Líkan', en: 'Model build' },
  kind_ifc: { is: 'IFC', en: 'IFC' },
  kind_deliverable: { is: 'Skilagagn', en: 'Deliverable' },
  kind_page: { is: 'Síða', en: 'Page' },
  kind_workspace: { is: 'Vinnuskrá', en: 'Workspace' },
  kind_other: { is: 'Annað', en: 'Other' },
  meta_title: { is: 'Titill', en: 'Title' },
  meta_sheetNumber: { is: 'Blaðnúmer', en: 'Sheet number' },
  meta_scale: { is: 'Mælikvarði', en: 'Scale' },
  meta_storey: { is: 'Hæð', en: 'Storey' },
  meta_status: { is: 'Staða', en: 'Status' },
  meta_date: { is: 'Dagsetning', en: 'Date' },
  meta_designer: { is: 'Hönnuður', en: 'Designer' },
  meta_archive: { is: 'Safn', en: 'Archive' },
  meta_case: { is: 'Mál', en: 'Case' },
  meta_type: { is: 'Gerð', en: 'Type' },
  meta_description: { is: 'Lýsing', en: 'Description' },
  meta_address: { is: 'Heimilisfang', en: 'Address' },
  meta_landNumber: { is: 'Landnúmer', en: 'Land number' },
  meta_pages: { is: 'Síður', en: 'Pages' },
  meta_scanned: { is: 'Skannað', en: 'Scanned' },
  meta_schema: { is: 'Skema', en: 'Schema' },
  meta_role: { is: 'Hlutverk', en: 'Role' },
  meta_products: { is: 'Hlutir', en: 'Products' },
  meta_validation: { is: 'Gilding', en: 'Validation' },
  meta_author: { is: 'Höfundur', en: 'Author' },
  meta_takenAt: { is: 'Tekin', en: 'Taken' },
  meta_source: { is: 'Uppruni', en: 'Source' },
  meta_surveyor: { is: 'Mælingamaður', en: 'Surveyor' },
  meta_pointCount: { is: 'Punktar', en: 'Points' },
  // Landing page (signed-out visitors at /).
  landingTitle: {
    is: 'Úr samþykktum teikningum\ní BIM-líkan.',
    en: 'From approved drawings\nto a BIM model.',
  },
  landingSub: {
    is: 'Líkan af byggðu húsi, unnið úr teikningunum en ekki úr uppmælingu. Fyrir eigendur og rekstraraðila fasteigna, arkitekta og verkfræðinga.',
    en: 'A model of an existing building, made from its approved drawings, not from a survey. For property owners and managers, architects and engineers.',
  },
  requestQuote: { is: 'Óska eftir tilboði', en: 'Request a quote' },
  youGet: { is: 'Það sem þú færð', en: 'What you get' },
  getIfc: { is: 'IFC-líkan', en: 'IFC model' },
  getIfcText: {
    is: 'IFC4 og IFC2X3 sem arkitekt getur tekið við.',
    en: 'IFC4 and IFC2X3 that an architect can take over.',
  },
  ifcWalls: { is: 'Heilir veggir með opum', en: 'Whole walls with openings' },
  ifcDoors: { is: 'Hurðir og gluggar', en: 'Doors and windows' },
  ifcStairs: { is: 'Stigar', en: 'Stairs' },
  ifcCurtain: { is: 'Glerveggir', en: 'Curtain walls' },
  ifcRooms: { is: 'Rými', en: 'Rooms as spaces' },
  ifcMaterials: {
    is: 'Efni þar sem teikningar tilgreina þau',
    en: 'Materials where the drawings state them',
  },
  ifcFire: { is: 'Brunamótstaða sem eiginleikar', en: 'Fire resistance as properties' },
  ifcGeo: { is: 'Hnitsett', en: 'Georeferenced' },
  ifcFurniture: { is: 'Húsgögn í sérskrá', en: 'Furniture in a separate file' },
  getViewer: { is: 'Vefskoðari', en: 'Web viewer' },
  getViewerText: {
    is: 'Líkanið í vafranum, með teikningarnar lagðar yfir.',
    en: 'The model in the browser, with the drawings overlaid.',
  },
  getDrawings: { is: 'Teikningar', en: 'Drawings' },
  getDrawingsText: {
    is: 'Grunnmyndir, snið og útlit dregin úr líkaninu.',
    en: 'Plans, sections and elevations generated from the model.',
  },
  getAreas: { is: 'Flatarmál', en: 'Areas' },
  getAreasText: { is: 'Reiknað eftir ÍST 50.', en: 'Calculated by ÍST 50.' },
  howTitle: { is: 'Ferlið', en: 'How it works' },
  step1: { is: 'Teikningar', en: 'Drawings' },
  step1Text: {
    is: 'Samþykktir aðaluppdrættir sóttir í skjalasafn sveitarfélagsins.',
    en: 'The approved drawings, fetched from the municipality’s archive.',
  },
  step2: { is: 'Líkan', en: 'Model' },
  step2Text: {
    is: 'Hver tala rekjanleg til teikningar. Sjálfvirkar prófanir og óháð yfirferð fyrir afhendingu.',
    en: 'Every number traceable to a sheet. Automated checks and independent reviews before delivery.',
  },
  step3: { is: 'Afhending', en: 'Hand-off' },
  step3Text: {
    is: 'Á þessum vef: þú skráir þig inn, opnar líkanið og sækir IFC-skrárnar.',
    en: 'On this site: sign in, open the model, download the IFC files.',
  },
  quoteLead: { is: 'Hvaða hús? Við svörum í tölvupósti.', en: 'Which building? We reply by email.' },
  quoteName: { is: 'Nafn', en: 'Name' },
  quoteCompany: { is: 'Fyrirtæki (valfrjálst)', en: 'Company (optional)' },
  quoteAddress: { is: 'Heimilisfang hússins', en: 'Address of the building' },
  quoteMessage: { is: 'Skilaboð (valfrjálst)', en: 'Message (optional)' },
  quoteSending: { is: 'Sendi…', en: 'Sending…' },
  quoteThanks: {
    is: 'Takk. Beiðnin er komin til Mynstru.',
    en: 'Thank you. Your request has reached Mynstra.',
  },
  quoteReplyTo: { is: 'Við svörum á {email}.', en: 'We will reply to {email}.' },
  quoteTooMany: {
    is: 'Of margar beiðnir frá þessu neti. Reyndu aftur eftir klukkustund.',
    en: 'Too many requests from this network. Try again in an hour.',
  },
  quoteInvalid: {
    is: 'Athugaðu reitina: nafn, netfang og heimilisfang hússins.',
    en: 'Check the fields: name, email and the building’s address.',
  },
  quoteFailed: {
    is: 'Beiðnin fór ekki. Athugaðu tenginguna og reyndu aftur.',
    en: 'The request was not sent. Check your connection and try again.',
  },
  company: { is: 'Mynstra ehf.', en: 'Mynstra ehf.' },
  quotePrivacy: {
    is: 'Við notum upplýsingarnar aðeins til að svara beiðninni.',
    en: 'We use these details only to answer your request.',
  },
} as const satisfies Record<string, Record<Lang, string>>;

export type Key = keyof typeof dict;

const STORAGE = 'p2b.lang';

function initialLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE);
    if (v === 'is' || v === 'en') return v;
  } catch {
    /* storage unavailable */
  }
  return 'is';
}

const Ctx = createContext<{ lang: Lang; setLang(l: Lang): void; t(k: Key): string }>({
  lang: 'is',
  setLang: () => {},
  t: (k) => dict[k].is,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE, l);
    } catch {
      /* storage unavailable */
    }
  }, []);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  const value = useMemo(() => ({ lang, setLang, t: (k: Key) => dict[k][lang] }), [lang, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n() {
  return useContext(Ctx);
}

/** Translated role or status label, falling back to the raw value. */
export function label(
  t: (k: Key) => string,
  prefix: 'role' | 'status' | 'kind' | 'meta' | 'location',
  value: string | null | undefined,
): string {
  if (!value) return '';
  const k = `${prefix}_${value}` as Key;
  return k in dict ? t(k) : value;
}

export function formatBytes(n: number, lang: Lang): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  const s = String(i === 0 ? Math.round(v) : Math.round(v * 10) / 10);
  return `${lang === 'is' ? s.replace('.', ',') : s} ${units[i]}`;
}

// Icelandic by hand: Chrome ships without Icelandic locale data, so Intl falls back to English there.
const IS_MONTHS = [
  'janúar',
  'febrúar',
  'mars',
  'apríl',
  'maí',
  'júní',
  'júlí',
  'ágúst',
  'september',
  'október',
  'nóvember',
  'desember',
];
const IS_MON = [
  'jan.',
  'feb.',
  'mar.',
  'apr.',
  'maí',
  'jún.',
  'júl.',
  'ágú.',
  'sep.',
  'okt.',
  'nóv.',
  'des.',
];
const pad = (n: number) => String(n).padStart(2, '0');

/** Date and time, e.g. "18. okt. 2026, 17:38" / "18 Oct 2026, 17:38". */
export function formatDate(iso: string, lang: Lang): string {
  const d = new Date(iso);
  if (lang === 'is')
    return `${d.getDate()}. ${IS_MON[d.getMonth()]} ${d.getFullYear()}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** The day only, e.g. "18. október 2026" / "18 October 2026". */
export function formatDay(iso: string, lang: Lang): string {
  const d = new Date(iso);
  if (lang === 'is') return `${d.getDate()}. ${IS_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Fill `{name}` placeholders in a translated string. */
export function fill(text: string, values: Record<string, string | number>): string {
  return text.replace(/\{(\w+)\}/g, (m, k: string) => (k in values ? String(values[k]) : m));
}
